import * as React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { Button, ConfirmButton, ButtonProps, Purchase, Default } from '@amzn/dvui/dist/Button'
import { Text } from '@amzn/dvui/dist/Text'
import { IDetailPageState } from '../../reducers'
import { CustomerPreferences } from '../../reducers/customerPreferences'
import { sanitizedRequestContextSelector } from '../../selectors/requestContext'
import { simpleSelectors } from '../../selectors/basicState'
import { withLogger, ILogger } from '../../context/withLogger'
import { safeBoolean, isTreatment} from '../../utils/utils'
import { CloseModal } from '@shared/utils/modalTrigger/ModalTrigger'
import * as utilityClasses from '../utilityClasses.module.scss'
import { triggerAuiPurchase } from '../../actions/sequences/auiPurchase'
import { triggerPurchase } from '../../actions/purchase'
import { offerTokenToPurchaseActionSelector } from '../../selectors/purchase'
import { isDeeplinkableActionReadySelector } from '../../selectors/pageContext'
import { camelToDataAttrs } from 'DVWebNodeUtils'
import { AtfTreatmentProps } from '../../reducers/features'
import * as cn from 'classnames'
import * as st from './acquisition.module.scss'
import { processDeeplinkFor } from '@shared/deeplink'
import { acknowledgeEvent } from '@shared/utils/events'

const COUNTER_PURCHASES_CORE_EXP_JS = 'DVWebNodeDetail-PurchaseCoreExpJs'

type ActionFormAttributes = {
  action: string
  id: string
  className: string
}

type ActionForm = {
  hiddenInputs: { [key: string]: string }
}

const ActionForm: React.ComponentType<
  ActionForm & ActionFormAttributes
> = ({ hiddenInputs = {}, children, id, ...otherProps }) => (
  <form {...otherProps} id={id} method="POST">
    {children}
    {Object.keys(hiddenInputs).map((name, idx) => (
      <input
        key={`ActionFormImput-${idx}`}
        type="hidden"
        name={name}
        value={hiddenInputs[name]}
      />
    ))}
  </form>
)

export enum TvodButtonScope {
  ACTION_BOX = 'ab',
  EPISODE_LIST = 'el',
  MORE_WAYS_TO_WATCH = 'mw',
  MORE_WAYS_TO_SUBSCRIBE = 'mws',
  EPISODE_LIST_MORE_WAYS_TO_WATCH = 'el_mw'
}

const offerTypes = {
  TVOD_RENTAL: 'vod',
  TVOD_PURCHASE: 'est',
  PREORDER: 'est'
}

const getRefTag = (
  scope: TvodButtonScope,
  { family, videoQuality = '', offerType }: WpsRuntime.atv.wps.PurchaseData
) => {
  const refTags = [
    'atv_dp_bb',
    offerTypes[offerType as (keyof typeof offerTypes)],
    videoQuality.toLowerCase(),
    family === 'MOVIE' ? 'movie' : 'tv',
    offerType === 'PREORDER' ? 'preorder' : '',
    scope
  ]

  return refTags.filter(Boolean).join('_')
}

const shouldShowWarning = (hasOptedOut: boolean, hasOfferWarning: boolean) =>
  !hasOptedOut && hasOfferWarning


type BuildDataAttributes = {
  asin: string
  purchaseData: WpsRuntime.atv.wps.PurchaseDataProps
  purchaseWarnings: WpsRuntime.atv.wps.PurchaseWarningsProps
  customerPreferences?: CustomerPreferences
  refTag: string
}
const buildDataAttributes = ({
  asin,
  purchaseData,
  purchaseWarnings,
  customerPreferences,
  refTag
}: BuildDataAttributes) => {
  const canShowWebHdWarning = shouldShowWarning(
    safeBoolean(customerPreferences && customerPreferences.optOutHdOnly),
    purchaseWarnings && safeBoolean(purchaseWarnings.showWebHdWarning)
  )
  const canShowWebHdcpWarning = shouldShowWarning(
    safeBoolean(customerPreferences && customerPreferences.optOutHdcp),
    purchaseWarnings && safeBoolean(purchaseWarnings.showWebHdcpWarning)
  )
  const showUnsupportedWarning = shouldShowWarning(
    safeBoolean(customerPreferences && customerPreferences.optOutUnsupportedDevice),
    purchaseWarnings && safeBoolean(purchaseWarnings.showUnsupportedWarning)
  )
  const showDeviceHdWarning = shouldShowWarning(
    safeBoolean(customerPreferences && customerPreferences.optOutHdOnly),
    purchaseWarnings && safeBoolean(purchaseWarnings.showDeviceHdWarning)
  )

  return camelToDataAttrs({
    asin,
    refTag,
    quality: purchaseData.videoQuality,
    signInUrl: purchaseData.signInUrl,
    isSeasonPurchase: safeBoolean(purchaseData.isSeasonPurchase),
    purchasingText: purchaseData.text,
    purchasingModalText: purchaseData.modalText,
    showDeviceHdWarning,
    canShowWebHdWarning,
    canShowWebHdcpWarning,
    showUnsupportedWarning
  })
}

export interface OwnProps {
  tvodAction: WpsRuntime.atv.wps.PurchaseActionProps
  scope: TvodButtonScope
  closeModal?: CloseModal
}

type TvodButtonType = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & OwnProps & ILogger & AtfTreatmentProps

const scrollTopIntoView = (canonicalTitleId: string, scope: TvodButtonScope) => {

  const triggeredFromEpisodeLists = scope === TvodButtonScope.EPISODE_LIST || scope === TvodButtonScope.EPISODE_LIST_MORE_WAYS_TO_WATCH

  if (typeof document.querySelector !== 'function' || !triggeredFromEpisodeLists) {
    return
  }

  const el = document.querySelector(`[for="selector-${canonicalTitleId}"]`) as HTMLElement & { scrollIntoViewIfNeeded?: Function }

  if (!el) {
    return
  }

  if (typeof el.scrollIntoViewIfNeeded === 'function') {
    el.scrollIntoViewIfNeeded(false)
  } else if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView(true)
  }
}

const buttonStyle = (style: string = 'DEFAULT') => {
  switch (style.toUpperCase()) {
    case 'HIGHLIGHTED': return Purchase
    default: return Default
  }
}

export const TvodButtonWithLogger = ({
  tvodAction,
  pageTitleId,
  customerPreferences,
  requestContext: { makeUrl },
  scope,
  logger,
  triggerAuiPurchase,
  triggerPurchase,
  useMFA,
  canonicalTitleId,
  closeModal,
  isDeeplinkableActionReady,
  useNodeDeeplinking,
  purchaseLaunchType,
  atfTreatment
}: TvodButtonType) => {
  const purchaseData = tvodAction && tvodAction.purchaseData

  if (!purchaseData) {
    return null
  }

  const atfTreatmentProps: ButtonProps = {
    ...(isTreatment(atfTreatment,'T1', 'T2', 'T3') && { forceDoubleHeight: false }),
    ...(isTreatment(atfTreatment,'T1', 'T3') && { shape: 'pill' })
  }

  if (purchaseLaunchType !== 'EMBEDDED') {
    if (!isDeeplinkableActionReady || !purchaseData.appPurchaseUrl || purchaseLaunchType === 'NONE') {
      return null
    }

    const refTag = getRefTag(scope, purchaseData)
    const appPurchaseUrl = `${purchaseData.appPurchaseUrl}&ref_=${refTag}`

    const handler = useNodeDeeplinking
      ? (e: React.MouseEvent) => {
            processDeeplinkFor[purchaseLaunchType]({
              url: appPurchaseUrl,
              fallbackUrl: purchaseData.appFallbackUrl,
            })
          e.preventDefault()
      } : undefined

    return (<Button
      href={appPurchaseUrl}
      className={cn('avu-full-width-phablet', {['js-deeplinkable']: !useNodeDeeplinking}, 'tvod-button', {[st.centerButton]: isTreatment(atfTreatment, 'T1', 'T2', 'T3')})}
      data-ux-click
      data-fallback-url={purchaseData.appFallbackUrl}
      onClick={handler}
      use={buttonStyle(tvodAction.style)}
      {...atfTreatmentProps}
    >
      <Text string={tvodAction.label} />
    </Button>)
  } else {
    const refTag = getRefTag(scope, purchaseData)
    const action = makeUrl('/purchase/process', { refTag })
    const hiddenInputs = {
      refTag,
      offerToken: tvodAction.offerToken,
      csrfToken: tvodAction.csrfToken,
      asin: tvodAction.asin
    }

    const dataAttrs = buildDataAttributes({
      asin: tvodAction.asin,
      purchaseWarnings: tvodAction.purchaseWarnings,
      purchaseData,
      customerPreferences,
      refTag
    })

    const handlePurchase = async () => {
      if (closeModal) {
        closeModal()
      }

      if (useMFA) {
        triggerPurchase(tvodAction.offerToken, refTag)
      } else {
        triggerAuiPurchase(
          logger,
          pageTitleId,
          canonicalTitleId,
          tvodAction,
          refTag,
          dataAttrs
        )
      }
    }

    const handleClick = async (e: React.MouseEvent<HTMLElement>) => {
      if (canonicalTitleId) {
        scrollTopIntoView(canonicalTitleId, scope)
      }
      acknowledgeEvent(e)
      handlePurchase()
      e.preventDefault()
    }

    const btnClassName = `tvod-btn-${tvodAction.asin}-${scope}`

    return (
      <ActionForm
        id={btnClassName}
        action={action}
        className={`${utilityClasses.fullWidthPhablet} ${st.buttonFormWrapper}`}
        hiddenInputs={hiddenInputs}
      >
        <ConfirmButton
          onlyConfirmOnTouchDevices
          onClick={handleClick}
          className={cn('dv-record-reftag tvod-button', {['av-button']: !tvodAction.style}, {[cn(utilityClasses.fullWidthPhablet, st.centerButton)]: isTreatment(atfTreatment, 'T1', 'T2', 'T3')})}
          {...dataAttrs}
          data-ux-click  // Track Interaction Fatal Rate with CSM
          type="submit"
          use={buttonStyle(tvodAction.style)}
          {...atfTreatmentProps}
        >
          <Text string={tvodAction.label} />
        </ConfirmButton>
        <div
          dangerouslySetInnerHTML={{
            __html: `<script>
              document.getElementById("${btnClassName}").addEventListener("submit",
                function() {
                  ue && ue.count('${COUNTER_PURCHASES_CORE_EXP_JS}', 1);
                  return true;
                }
              )
            </script>`,
          }}
        />
      </ActionForm>
    )
  }
}


const mapStateToProps = (state: IDetailPageState, ownProps: OwnProps) => {
  const purchaseAction = offerTokenToPurchaseActionSelector(state)[
    ownProps.tvodAction.offerToken
  ]
  return {
    requestContext: sanitizedRequestContextSelector(state),
    customerPreferences: simpleSelectors.preferences(state),
    useMFA: state.features.useMFA,
    pageTitleId: state.pageTitleId,
    canonicalTitleId: purchaseAction && purchaseAction.canonicalId,
    isDeeplinkableActionReady: isDeeplinkableActionReadySelector(state),
    useNodeDeeplinking: state.features.useNodeDeeplinking,
    purchaseLaunchType: state.pageContext.purchaseLaunchType
  }
}

const mapDispatchToProps = (
  dispatch: Dispatch,
  { logger }: OwnProps & ILogger
) => ({
  triggerAuiPurchase: triggerAuiPurchase(dispatch),
  triggerPurchase: (offerToken: string, refTag: string) =>
    triggerPurchase(dispatch, logger, offerToken, undefined, refTag),
})

export const TvodButton = withLogger(connect(
  mapStateToProps,
  mapDispatchToProps
)(TvodButtonWithLogger))